import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, startOfWeek, isSameDay, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { getBookings } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Calendar as CalendarIcon, 
  MapPin, 
  Clock, 
  ChevronLeft, 
  ChevronRight,
  FileText,
  Lightbulb,
  User,
  Menu,
  LogOut,
  ExternalLink,
  Phone,
  Mail,
  Navigation,
  ShoppingCart,
  ClipboardList,
  ArrowLeft
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const CalendarPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  useEffect(() => {
    loadBookings();
  }, [selectedDate]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await getBookings(dateStr);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevWeek = () => {
    setSelectedDate(addDays(selectedDate, -7));
  };

  const handleNextWeek = () => {
    setSelectedDate(addDays(selectedDate, 7));
  };

  const formatTime = (isoString) => {
    try {
      return format(parseISO(isoString), 'HH:mm');
    } catch {
      return '--:--';
    }
  };

  // Generate Google Maps link
  const getGoogleMapsUrl = (booking) => {
    const address = [
      booking.address_line,
      booking.postal_code,
      booking.city
    ].filter(Boolean).join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - sticky with safe area for iOS PWA */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-10 safe-top">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/')}
              data-testid="back-btn"
              className="mr-1"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
              <span className="text-sm font-bold text-white font-outfit">GB</span>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-slate-900 font-outfit">
                Gardinbussen Intra
              </h1>
              <p className="text-xs text-slate-500">{user?.name}</p>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                  <User className="mr-2 h-4 w-4" />
                  Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Log ud
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Week Navigation */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" onClick={handlePrevWeek} data-testid="prev-week-btn">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-lg font-medium text-slate-900">
              {format(weekStart, "'Uge' w, MMMM yyyy", { locale: da })}
            </h2>
            <Button variant="ghost" size="icon" onClick={handleNextWeek} data-testid="next-week-btn">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* Week Days */}
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((day, index) => {
              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              
              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(day)}
                  className={`
                    flex flex-col items-center py-3 rounded-xl transition-all
                    ${isSelected 
                      ? 'bg-slate-900 text-white' 
                      : isToday 
                        ? 'bg-teal-50 text-teal-700' 
                        : 'hover:bg-slate-100 text-slate-600'
                    }
                  `}
                  data-testid={`day-${format(day, 'yyyy-MM-dd')}`}
                >
                  <span className="text-xs font-medium uppercase">
                    {format(day, 'EEE', { locale: da })}
                  </span>
                  <span className={`text-lg font-semibold ${isSelected ? 'text-white' : ''}`}>
                    {format(day, 'd')}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bookings List */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-6">
          <CalendarIcon className="h-5 w-5 text-slate-400" />
          <h3 className="text-lg font-semibold text-slate-900">
            {format(selectedDate, "EEEE 'd.' d MMMM", { locale: da })}
          </h3>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-slate-100">
                <CardContent className="p-5">
                  <Skeleton className="h-6 w-32 mb-3" />
                  <Skeleton className="h-4 w-48 mb-2" />
                  <Skeleton className="h-4 w-64" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : bookings.length === 0 ? (
          <Card className="border-slate-100 border-dashed">
            <CardContent className="p-12 text-center">
              <CalendarIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Ingen bookinger denne dag</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Card 
                key={booking.id}
                className="border-slate-100 hover:shadow-md transition-shadow"
                data-testid={`booking-card-${booking.hubhus_booking_id}`}
              >
                <CardContent className="p-5">
                  {/* Header row with time and badges */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-slate-900">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span className="text-xl font-semibold font-outfit">
                          {formatTime(booking.scheduled_start)}
                        </span>
                      </div>
                      {booking.scheduled_end && (
                        <span className="text-slate-400">
                          – {formatTime(booking.scheduled_end)}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Badge 
                        variant={booking.survey_status === 'received' ? 'default' : 'outline'}
                        className={booking.survey_status === 'received' 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                          : 'text-slate-500'
                        }
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Survey
                      </Badge>
                      <Badge 
                        variant={booking.coaching_status === 'ready' ? 'default' : 'outline'}
                        className={booking.coaching_status === 'ready' 
                          ? 'bg-teal-50 text-teal-700 border-teal-100' 
                          : 'text-slate-500'
                        }
                      >
                        <Lightbulb className="h-3 w-3 mr-1" />
                        Coaching
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Customer name */}
                  <h4 className="text-lg font-medium text-slate-900 mb-2">
                    {booking.customer_name}
                  </h4>
                  
                  {/* Address with map link */}
                  {booking.address_line && (
                    <a 
                      href={getGoogleMapsUrl(booking)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-slate-600 text-sm hover:text-teal-700 transition-colors mb-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MapPin className="h-4 w-4 flex-shrink-0" />
                      <span className="hover:underline">
                        {booking.address_line}
                        {booking.postal_code && `, ${booking.postal_code}`}
                        {booking.city && ` ${booking.city}`}
                      </span>
                      <Navigation className="h-3 w-3 ml-1" />
                    </a>
                  )}
                  
                  {/* Contact info row */}
                  <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
                    {booking.customer_phone && (
                      <a 
                        href={`tel:${booking.customer_phone}`}
                        className="flex items-center gap-1.5 text-slate-600 hover:text-teal-700 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="h-4 w-4" />
                        <span>{booking.customer_phone}</span>
                      </a>
                    )}
                    {booking.customer_email && (
                      <a 
                        href={`mailto:${booking.customer_email}`}
                        className="flex items-center gap-1.5 text-slate-600 hover:text-teal-700 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Mail className="h-4 w-4" />
                        <span className="truncate max-w-[200px]">{booking.customer_email}</span>
                      </a>
                    )}
                  </div>
                  
                  {/* Booking type and action buttons */}
                  <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
                    {booking.booking_type && (
                      <Badge variant="outline" className="text-xs capitalize">
                        {booking.booking_type}
                      </Badge>
                    )}
                    
                    {/* Magento order link - for montering */}
                    {booking.booking_type === 'montering' && booking.asset_processing_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(booking.asset_processing_url, '_blank');
                        }}
                        data-testid={`asset-link-${booking.hubhus_booking_id}`}
                      >
                        <ShoppingCart className="h-3 w-3 mr-1" />
                        Ordre i ASSET
                      </Button>
                    )}
                    
                    {/* Magento customer link - for opmåling with conversion */}
                    {booking.conversion_magento_customer_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(booking.conversion_magento_customer_url, '_blank');
                        }}
                        data-testid={`magento-customer-${booking.hubhus_booking_id}`}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Kunde i ASSET
                      </Button>
                    )}
                    
                    {/* Prep/coaching link - show for opmåling and other sales-related booking types */}
                    {booking.booking_type && !['montering', 'ferie'].includes(booking.booking_type.toLowerCase()) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className={`h-7 text-xs ${booking.coaching_status === 'ready' 
                          ? 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100' 
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/prep/${booking.hubhus_booking_id}`);
                        }}
                        data-testid={`prep-link-${booking.hubhus_booking_id}`}
                      >
                        <Lightbulb className="h-3 w-3 mr-1" />
                        {booking.coaching_status === 'ready' ? 'Se coaching' : 'Forbered'}
                      </Button>
                    )}
                    
                    {/* Survey details link */}
                    {booking.survey_status === 'received' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/booking/${booking.hubhus_booking_id}?tab=survey`);
                        }}
                        data-testid={`survey-link-${booking.hubhus_booking_id}`}
                      >
                        <ClipboardList className="h-3 w-3 mr-1" />
                        Se survey
                      </Button>
                    )}
                  </div>
                  
                  {/* Notes/special info if present */}
                  {booking.notes && (
                    <div className="mt-3 p-2 bg-amber-50 rounded-lg text-sm text-amber-800">
                      <strong>Note:</strong> {booking.notes}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CalendarPage;
